import { useState } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { GiSpiralBottle } from 'react-icons/gi';
import SOSModal from './SOSModal';
import { UpgradeModal } from '../payment/UpgradeModal';
import { featureGateService } from '../../services/featureGateService';
import { useAuth } from '../../contexts/AuthContext';
import { useSubscription } from '../../hooks/useSubscription';

interface NavItem {
  path: string;
  icon: string;
  activeIcon: string;
  label: string;
}

export default function BottomNavigation() {
  const location = useLocation();
  const { user } = useAuth();
  const { subscription } = useSubscription();
  const [showSOSModal, setShowSOSModal] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [featureUsage, setFeatureUsage] = useState({ used: 0, limit: 3 });

  const isActive = (path: string) => {
    if (path === '/dashboard' && (location.pathname === '/' || location.pathname === '/dashboard')) {
      return true;
    }
    return location.pathname === path;
  };

  const navigationItems: NavItem[] = [
    {
      path: '/dashboard',
      icon: 'ri-home-4-line',
      activeIcon: 'ri-home-4-fill',
      label: 'Início'
    },
    {
      path: '/remedies',
      icon: 'spiral-bottle',
      activeIcon: 'spiral-bottle',
      label: 'Remédios'
    },
    {
      path: '/profile',
      icon: 'ri-user-heart-line',
      activeIcon: 'ri-user-heart-fill',
      label: 'Perfil'
    },
    {
      path: '/settings',
      icon: 'ri-settings-3-line',
      activeIcon: 'ri-settings-3-fill',
      label: 'Configurações'
    }
  ];

  const handleSOSClick = async () => {
    if (!user) return;

    // Verificar se usuário pode usar SOS
    const access = await featureGateService.canUseFeature(user.id, 'sos_consultation');
    
    if (!access.allowed) {
      // Limite atingido - mostrar modal de upgrade
      setFeatureUsage({ 
        used: access.used || 0, 
        limit: access.limit || 3 
      });
      setShowUpgradeModal(true);
      return;
    }

    // Pode usar - abre modal SOS
    setShowSOSModal(true);
  };

  return (
    <>
      <div className="fixed bottom-0 left-0 right-0 z-40">
        <div className="mx-auto max-w-md px-3 pb-0.5">
          <div className="relative overflow-visible rounded-t-[22px] border-t border-gray-100 bg-white shadow-[0_-5px_16px_RGBA(15,23,42,0.12)] pt-5 pb-0.5">
            <div className="grid grid-cols-4 items-center gap-0.5">
              {navigationItems.map((item) => {
                const offsetClass =
                  item.path === '/remedies'
                    ? ''
                    : item.path === '/profile'
                      ? ''
                      : '';

                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`flex flex-col items-center gap-1 font-sans transition-colors ${
                      isActive(item.path) ? 'text-emerald-600' : 'text-slate-400'
                    } ${offsetClass}`}
                    data-tutorial-anchor={item.path === '/settings' ? 'nav-settings' : item.path === '/dashboard' ? 'nav-home' : undefined}
                  >
                    <span className="flex h-11 w-11 items-center justify-center rounded-full">
                      {item.icon === 'spiral-bottle' ? (
                        <GiSpiralBottle
                          className={`${isActive(item.path) ? 'text-emerald-600' : 'text-slate-400'} text-[30px]`}
                        />
                      ) : (
                        <i className={`${isActive(item.path) ? item.activeIcon : item.icon} text-[26px]`}></i>
                      )}
                    </span>
                    <span className="text-xs font-semibold tracking-tight">{item.label}</span>
                  </Link>
                );
              })}
            </div>
            <div className="absolute left-1/2 -top-[30px] flex -translate-x-1/2 flex-col items-center">
              <button
                onClick={handleSOSClick}
                className="flex h-[64px] w-[64px] items-center justify-center rounded-full bg-gradient-to-br from-red-500 via-red-500 to-red-600 text-white shadow-[0_12px_28px_RGBA(220,38,38,0.45)] transition-transform hover:-translate-y-1 hover:shadow-[0_16px_34px_RGBA(220,38,38,0.55)] focus:outline-none focus:ring-4 focus:ring-red-200"
              >
                <i className="ri-heart-pulse-line text-3xl"></i>
              </button>
              <span className="mt-1 text-[11px] font-semibold uppercase tracking-wide text-red-500">SOS</span>
            </div>
          </div>
        </div>
      </div>

      {/* SOS Modal */}
      <SOSModal 
        isOpen={showSOSModal} 
        onClose={() => setShowSOSModal(false)} 
      />

      {/* Upgrade Modal */}
      {showUpgradeModal && (
        <UpgradeModal
          featureKey="sos_consultation"
          featureName="Consultas SOS"
          currentPlan={subscription?.plan_tier || 'free'}
          usage={featureUsage}
          onClose={() => setShowUpgradeModal(false)}
        />
      )}
    </>
  );
}